import {
  Menu as MenuBase,
  MenuItem as MenuItemBase,
  MenuTrigger,
  Popover,
  Separator as SeparatorBase,
  MenuSection as MenuSectionBase,
  Header as HeaderBase,
  type MenuItemProps,
  type MenuProps,
  type MenuTriggerProps,
} from "react-aria-components";
import { FaAngleRight, FaCheck } from "react-icons/fa6";
import { css, cx } from "../../../../styled-system/css";
import { styled } from "../../../../styled-system/jsx";
import type React from "react";
import { Button } from "./Button";
import { popoverStyles } from "../../fields/styles/styles";

interface Props<T> extends Omit<MenuProps<T>, "children">, MenuTriggerProps {
  label: React.ReactNode;
  buttonProps?: Omit<React.ComponentProps<typeof Button>, "children">;
  popoverProps?: Omit<React.ComponentProps<typeof Popover>, "children"> & { className?: string };
}

export function MenuButton<T extends object>({ label, children, buttonProps, popoverProps, ...props }: Props<T>) {
  return (
    <MenuTrigger {...props}>
      <Button {...buttonProps}>{label}</Button>
      <Popover
        offset={3}
        {...popoverProps}
        className={cx(
          popoverStyles,
          css({
            border: "1px solid {colors.border.default}",
            borderRadius: "standard",
            minWidth: "50px",
            paddingY: "xsmall",
            boxShadow: "card",
          }),
          popoverProps?.className,
        )}
      >
        {children}
      </Popover>
    </MenuTrigger>
  );
}

export const Menu = MenuBase;
export const MenuSection = MenuSectionBase;

export const MenuSectionHeader = styled(HeaderBase, {
  base: {
    paddingY: "xsmall",
    paddingX: "medium",
    paddingTop: "small",
    color: "text.secondary",
    fontSize: "small",
    fontWeight: "bold",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
});

export const Separator = styled(SeparatorBase, {
  base: {
    alignSelf: "stretch",
    borderTop: "1px solid {colors.border.default}",
    height: "0",
    marginY: "small",
    marginX: "medium",
    opacity: 0.6,
  },
});

export function MenuItem(props: MenuItemProps & { children?: React.ReactNode; className?: string }) {
  const textValue = props.textValue || (typeof props.children === "string" ? props.children : undefined);
  return (
    <MenuItemBase
      {...props}
      textValue={textValue}
      className={cx(
        css({
          cursor: "pointer",
          display: "flex",
          gap: "xsmall",
          alignItems: "center",
          border: "none",
          outline: "none",
          borderRadius: "light",
          marginX: "xsmall",
          paddingY: "xsmall",
          paddingX: "medium",
          fontSize: "medium",
          transition: "all 0.15s ease",
          _disabled: {
            cursor: "not-allowed",
            color: "text.secondary",
            opacity: 0.5,
          },
          _selected: {
            color: "trellisPalette.0",
            fontWeight: "600",
            backgroundColor: "trellisPalette.0/15",
          },
          _focusVisible: {
            outline: "2px solid {colors.trellisPalette.0/30}",
            outlineOffset: "-1px",
            backgroundColor: "trellisPalette.0/10",
          },
          _hover: {
            backgroundColor: "trellisPalette.0/10",
          },
        }),
        props.className,
      )}
    >
      {({ hasSubmenu, isSelected }) => (
        <>
          {isSelected && <FaCheck />}
          {props.children}
          {hasSubmenu && <FaAngleRight />}
        </>
      )}
    </MenuItemBase>
  );
}
